import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from 'store/rootStore';
import { formUtils } from '@insurancemenu/imenu-components-lib';
import { FormObjectFromJSON } from '@insurancemenu/imenu-components-lib/dist/types';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { Cart } from 'types/cartService';
import { BrowsingInfo, CartBrowsingCriteria } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';

const INITIAL_FORM_OBJECT: Readonly<FormObjectFromJSON> = Object.freeze({
  id: '',
  formTitle: '',
  sections: [],
  description: '',
});

const INITIAL_BC_OBJECT: Readonly<CartBrowsingCriteria> = Object.freeze({
  effectiveDate: '',
  zipCode: '',
  state: '',
  county: '',
  shortName: {},
});

export class BrowsingCriteriaModalStore {
  rootStore: RootStore;
  cartId: string;
  form: FormObjectFromJSON;
  dynamicForm: null | formUtils.DynamicFormController;
  loading = false;
  showBrowsingCriteriaModal = false;
  productLineId: ProductLineId = 'GroupDental';
  browsingCriteria: CartBrowsingCriteria;

  setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async openBrowsingCriteriaModal(
    cart: Cart,
    productLineId: ProductLineId,
    formId: string,
    additionalBcByPLine?: Record<string, string>,
  ): Promise<void> {
    this.setLoading(true);
    this.showBrowsingCriteriaModal = true;
    this.productLineId = productLineId;
    this.cartId = cart.id;
    this.dynamicForm = null;
    this.form = INITIAL_FORM_OBJECT;

    const { effectiveDate, zipCode, state, county, naics, sic2, sic4 } = cart.lastUpdateBrowsingCriteria;
    const convertedCartBc = {
      [productLineId]: [
        {
          ...cart.lastUpdateBrowsingCriteria[productLineId],
          effectiveDate,
          zipCode,
          state,
          county,
          naics,
          sic2,
          sic4,
        },
      ],
    };

    const res = await this.rootStore.formStore.cachedRequestFormById(formId, convertedCartBc);
    runInAction(() => {
      this.form = res.data.getFormById;
      this.browsingCriteria = cart.lastUpdateBrowsingCriteria;
      const productLineAnswers = additionalBcByPLine
        ? { ...cart.lastUpdateBrowsingCriteria[productLineId], ...additionalBcByPLine }
        : cart.lastUpdateBrowsingCriteria[productLineId];
      this.createFormController(productLineAnswers);
      this.setLoading(false);
    });
  }

  closeBrowsingCriteriaModal(): void {
    this.showBrowsingCriteriaModal = false;
  }

  createFormController(answers?: Record<string, string>): void {
    const params = formUtils.createDynamicFormControllerParamsFromJSON(this.form);
    this.dynamicForm = new formUtils.DynamicFormController(params.fields, params.enableMastersObject);
    if (answers) {
      this.dynamicForm.populateFields(answers);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async saveBrowsingCriteria(
    additionalBc: Record<string, string> | null = null,
    productLineId: ProductLineId | null = null,
  ): Promise<Cart> {
    this.setLoading(true);
    if (!this.browsingCriteria[this.productLineId]) {
      this.browsingCriteria[this.productLineId] = {};
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    Object.keys(this.dynamicForm!.fields).forEach((key) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      (this.browsingCriteria[this.productLineId] as BrowsingInfo)[key] =
        this.dynamicForm!.fields[key].value!.toString();
    });

    if (additionalBc && productLineId) {
      const bcByProductLine = this.browsingCriteria[this.productLineId];
      this.browsingCriteria[this.productLineId] = { ...bcByProductLine, ...additionalBc };
    }

    const res = await this.rootStore.cartStore.updateBC(
      this.cartId,
      this.browsingCriteria,
      this.rootStore.domainStore.domainHeader.id,
    );
    return runInAction(() => {
      if (!res.data) {
        throw new Error(`Updating browsing criteria for ${this.cartId} wasn't successful`);
      }
      this.setLoading(false);
      return res.data.updateLastBC;
    });
  }

  // eslint-disable-next-line class-methods-use-this
  async updateBrowsingCriteriaWithoutForm(
    cart: Cart,
    additionalBc: Record<string, string> | null = null,
    productLineId: ProductLineId | null = null,
  ): Promise<Cart> {
    this.setLoading(true);
    this.productLineId = productLineId || 'GroupDental';
    this.browsingCriteria = cart.lastUpdateBrowsingCriteria;
    if (!this.browsingCriteria[this.productLineId]) {
      this.browsingCriteria[this.productLineId] = {};
    }

    if (additionalBc && productLineId) {
      const bcByProductLine = this.browsingCriteria[this.productLineId];
      this.browsingCriteria[this.productLineId] = { ...bcByProductLine, ...additionalBc };
    }

    const res = await this.rootStore.cartStore.updateBC(
      cart.id,
      this.browsingCriteria,
      this.rootStore.domainStore.domainHeader.id,
    );
    return runInAction(() => {
      if (!res.data) {
        throw new Error(`Updating browsing criteria for ${this.cartId} wasn't successful`);
      }
      this.setLoading(false);
      return res.data.updateLastBC;
    });
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.cartId = '';
    this.dynamicForm = null;
    this.form = INITIAL_FORM_OBJECT;
    this.browsingCriteria = INITIAL_BC_OBJECT;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
